import { useCallback, useEffect, useRef, useState } from "react";
import type RTKClient from "@cloudflare/realtimekit";
import type { Message } from "@cloudflare/realtimekit";
import {
  LIVESTREAM_PINNED_COMMENT_MESSAGE_TYPE,
  LIVESTREAM_PINNED_COMMENT_SYNC_REQUEST_MESSAGE_TYPE,
  buildLivestreamPinnedCommentClearPayload,
  buildLivestreamPinnedCommentPayload,
  buildLivestreamPinnedCommentSyncRequestPayload,
  parseLivestreamPinnedComment,
  parseLivestreamPinnedCommentClear,
  parseLivestreamPinnedCommentSyncRequest,
  type LivestreamPinnedComment,
} from "./livestreamPinnedComment";

export function useLivestreamPinnedComment({
  meeting,
  isHost,
  messages,
}: {
  meeting: RTKClient | undefined;
  isHost: boolean;
  messages: Message[];
}) {
  const [pinnedComment, setPinnedComment] = useState<LivestreamPinnedComment | null>(null);
  const pinnedCommentRef = useRef<LivestreamPinnedComment | null>(null);

  useEffect(() => {
    pinnedCommentRef.current = pinnedComment;
  }, [pinnedComment]);

  const broadcastPinnedComment = useCallback(
    async ({
      comment,
      peerIds,
    }: {
      comment: LivestreamPinnedComment | null;
      peerIds?: string[];
    }) => {
      if (!meeting?.participants) return;

      await meeting.participants.broadcastMessage(
        LIVESTREAM_PINNED_COMMENT_MESSAGE_TYPE,
        comment
          ? buildLivestreamPinnedCommentPayload({ comment })
          : buildLivestreamPinnedCommentClearPayload(),
        peerIds && peerIds.length > 0 ? { participantIds: peerIds } : undefined,
      );
    },
    [meeting],
  );

  const requestPinnedCommentSync = useCallback(async () => {
    if (isHost || !meeting?.participants) return;

    const participantId = meeting.self.id;
    if (!participantId) return;

    await meeting.participants.broadcastMessage(
      LIVESTREAM_PINNED_COMMENT_SYNC_REQUEST_MESSAGE_TYPE,
      buildLivestreamPinnedCommentSyncRequestPayload({ participantId }),
    );
  }, [isHost, meeting]);

  useEffect(() => {
    if (!meeting?.participants) return;

    const onBroadcastedMessage = ({
      type,
      payload,
    }: {
      type: string;
      payload: Record<string, boolean | number | string | Date>;
    }) => {
      if (type === LIVESTREAM_PINNED_COMMENT_MESSAGE_TYPE) {
        if (parseLivestreamPinnedCommentClear({ payload })) {
          setPinnedComment(null);
          return;
        }
        const comment = parseLivestreamPinnedComment({ payload });
        if (comment) {
          setPinnedComment(comment);
        }
        return;
      }

      if (!isHost || type !== LIVESTREAM_PINNED_COMMENT_SYNC_REQUEST_MESSAGE_TYPE) return;

      const participantId = parseLivestreamPinnedCommentSyncRequest({ payload });
      if (!participantId) return;

      void broadcastPinnedComment({
        comment: pinnedCommentRef.current,
        peerIds: [participantId],
      });
    };

    meeting.participants.on("broadcastedMessage", onBroadcastedMessage);
    return () => {
      meeting.participants.off("broadcastedMessage", onBroadcastedMessage);
    };
  }, [broadcastPinnedComment, isHost, meeting]);

  useEffect(() => {
    if (!isHost || !pinnedComment) return;

    const pinnedStillExists = messages.some((message) => message.id === pinnedComment.messageId);
    if (pinnedStillExists) return;

    setPinnedComment(null);
    void broadcastPinnedComment({ comment: null });
  }, [broadcastPinnedComment, isHost, messages, pinnedComment]);

  const pinComment = useCallback(
    async ({
      messageId,
      displayName,
      text,
    }: {
      messageId: string;
      displayName: string;
      text: string;
    }) => {
      if (!isHost) return;

      const comment: LivestreamPinnedComment = { messageId, displayName, text };
      setPinnedComment(comment);
      await broadcastPinnedComment({ comment });
    },
    [broadcastPinnedComment, isHost],
  );

  const unpinComment = useCallback(async () => {
    if (!isHost || !pinnedCommentRef.current) return;

    setPinnedComment(null);
    await broadcastPinnedComment({ comment: null });
  }, [broadcastPinnedComment, isHost]);

  const togglePinComment = useCallback(
    async ({
      messageId,
      displayName,
      text,
    }: {
      messageId: string;
      displayName: string;
      text: string;
    }) => {
      if (!isHost) return;

      if (pinnedCommentRef.current?.messageId === messageId) {
        await unpinComment();
        return;
      }

      await pinComment({ messageId, displayName, text });
    },
    [isHost, pinComment, unpinComment],
  );

  return {
    pinnedComment,
    pinComment,
    unpinComment,
    togglePinComment,
    requestPinnedCommentSync,
  };
}
